import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, TextField, Typography } from '@mui/material';
import { Badge } from 'antd';
import { useSnackbar } from 'notistack';
import React, { useContext, useState } from 'react';
import { updateGoldenRecord } from "../../api's/DataProcessorApi";
import { WorkflowContext } from '../../contexts/WorkflowProvider';

function DataQualityDataManager({ row, prevNodeId }) {
  const [formData, setFormData] = useState(row);
  const [dirtyFields, setDirtyFields] = useState({}); // Track dirty fields
  const { workflow } = useContext(WorkflowContext);
  const { enqueueSnackbar } = useSnackbar();

  // Handle change in input fields
  const handleInputChange = (id, key, value) => {
    setFormData((prevData) => prevData.map((item) => (item.id === id ? { ...item, [key]: value } : item)));
    setDirtyFields((prevDirtyFields) => ({
      ...prevDirtyFields,
      [id]: { ...(prevDirtyFields[id] || {}), [key]: true }, // Mark the field as dirty
    }));
  };

  // Validation helper to check if value is not null, not empty, and not only spaces
  const isValidField = (value) => {
    return value !== null && value.trim() !== ''; // Check for non-null, non-empty, and no space-only values
  };

  // Handle Update row button click
  const handleUpdate = async () => {
    console.log('Updated Data:', formData);
    console.log('Original Row Data:', row);

    try {
      const data = {
        nodeId: prevNodeId,
        groupId: row[0].group_id,
        issueListType: 'LLMIssues',
        update: {},
      };

      let isValid = true; // To track if form is valid

      // Loop through the formData to find changed and valid fields
      formData.forEach((item, index) => {
        const originalItem = row[index];
        const editableFields = JSON.parse(item?.failed_rule_json).map((rule) => rule.fieldName);

        Object.keys(item).forEach((field) => {
          if (editableFields.includes(field)) {
            // Field is editable, check if it has been changed and is valid
            if (dirtyFields[item.id]?.[field] && item[field] !== originalItem[field]) {
              if (!isValidField(item[field])) {
                isValid = false; // Mark form as invalid
                enqueueSnackbar(`Invalid value for field: ${field}`, {
                  variant: 'warning',
                  autoHideDuration: 3000,
                  anchorOrigin: { vertical: 'top', horizontal: 'right' },
                });
              } else {
                // If valid, include field in update
                data.update[field] = item[field];
              }
            }
          }
        });
      });

      // Stop if form is not valid
      if (!isValid) {
        return;
      }

      // Check if there are any changes before making the API call
      if (Object.keys(data.update).length === 0) {
        enqueueSnackbar('No changes detected', {
          variant: 'info',
          autoHideDuration: 1500,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        return;
      }

      // Make the API call with the correct payload
      const response = await updateGoldenRecord(data);
      if (response.status === 200) {
        enqueueSnackbar('Row Saved!', {
          variant: 'success',
          autoHideDuration: 1500,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    } catch (error) {
      enqueueSnackbar('Error while saving!', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
  };

  return (
    <>
      <div style={{ margin: '20px 10px' }}>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ backgroundColor: '#e8f8f5' }}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography>
              <h3>Field Validation Overview</h3>
            </Typography>
          </AccordionSummary>
          <hr />
          <AccordionDetails sx={{ paddingTop: '16px' }}>
            {formData.map((item) => {
              const failedFields = JSON.parse(item?.failed_rule_json).map((rule) => {
                const fieldName = rule.fieldName;
                const statement = rule.statement;
                const failedRules = rule['Failed Rules'];

                const ruleStatements = failedRules.map((rule) => {
                  switch (rule) {
                    case 'Completeness':
                      return 'must be complete';
                    case 'Uniqueness':
                      return 'must be unique';
                    case 'Accuracy':
                      return 'must be accurate';
                    case 'Validity':
                      return 'must be valid';
                    case 'Timeliness':
                      return 'must be timely';
                    case 'Consistency':
                      return 'must be consistent';
                    default:
                      return rule;
                  }
                });

                return (
                  <Typography key={fieldName} sx={{ marginBottom: '10px' }}>
                    <Badge color="#f50" />{' '}
                    {/* <div> */}
                      <strong>{fieldName}</strong> {ruleStatements.join(' and ')}
                      <br /><span style={{color:'#616a6b'}}>{statement}</span>
                    {/* </div> */}
                  </Typography>
                );
              });

              return <div key={item.id}>{failedFields}</div>;
            })}
          </AccordionDetails>
        </Accordion>
      </div>
      <div>
        {formData.map((item) => {
          const failedFields = JSON.parse(item?.failed_rule_json).map((rule) => rule.fieldName);

          return (
            <div key={item.id}>
              {Object.entries(item).map(([key, value]) => {
                if (key === 'failed_rule_json') return null;

                return (
                  <TextField
                    key={key}
                    label={key}
                    size="small"
                    value={typeof value === 'object' ? JSON.stringify(value) : value}
                    onChange={(e) => handleInputChange(item.id, key, e.target.value)}
                    disabled={!failedFields.includes(key)}
                    sx={{
                      backgroundColor: '#ffffff',
                      width: '20%',
                      marginTop: '10px',
                      marginRight: '10px',
                    }}
                  />
                );
              })}
            </div>
          );
        })}

        <Button variant="contained" color="primary" onClick={handleUpdate} sx={{ margin: '10px 0 0 10px' }}>
          Update row
        </Button>
      </div>
    </>
  );
}

export default DataQualityDataManager;
